import * as React from "react";
import { Icon } from "@fluentui/react";
// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { BaseComponent } from "../../BaseComponent";
// Utilities
import { css } from "@fluentui/utilities/lib/css";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { getClassNames, getStyles } from "./CompleteTaskAnimationButton.styles";
import { progressNotStartedIconProps, completedIconProps, progressCompleteIconProps, circleSparkleIconProps } from "../../../icons/icons.styles";
export class CompleteTaskAnimationButton extends BaseComponent {
    /**
     * React lifecycle method called after component updates
     */ componentDidUpdate(prevProps, prevState) {
        if (prevProps.isTaskCompleted !== this.props.isTaskCompleted && this.props.isTaskCompleted !== this.state.isTaskCompleted && prevProps.id === this.props.id) {
            // Plays the animation is the task complete changes from elsewhere (such as through diffsync)
            // Only play the animation if the same task is being rendered
            this.playCssAnimation(null, false);
        }
    }
    /**
     * Name of the component
     */ name() {
        return "CompleteTaskAnimationButton";
    }
    renderComponent() {
        const { isTaskCompleted } = this.state;
        const { isDisabled, completeTaskButtonTabIndex, isAITask } = this.props;
        const classNames = this.getClassNames();
        const handleClick = (evt)=>{
            if (isDisabled) {
                return;
            }
            this.playCssAnimation(evt, true);
        };
        const handleKeyUp = (evt)=>{
            if (isDisabled || evt.keyCode !== KeyCode.ENTER && evt.keyCode !== KeyCode.SPACE) {
                return;
            }
            evt?.preventDefault();
            this.playCssAnimation(evt, true);
        };
        const notCompletedIconProps = isAITask ? circleSparkleIconProps : progressNotStartedIconProps;
        const regularCompleteIconProps = isTaskCompleted ? progressCompleteIconProps : notCompletedIconProps;
        const hoveredCompleteIconProps = isTaskCompleted ? progressCompleteIconProps : completedIconProps;
        return /*#__PURE__*/ React.createElement("button", {
            className: classNames.root,
            onClick: handleClick,
            onKeyUp: handleKeyUp,
            onKeyDown: (evt)=>{
                this.props.disableTaskCardFocusZone?.(evt);
                if (evt.keyCode === KeyCode.ENTER || evt.keyCode === KeyCode.SPACE) {
                    evt?.stopPropagation();
                    evt?.preventDefault(); // prevents the button from continuously calling the callback when enter is held down
                }
            },
            "aria-label": this.props.ariaLabel || (isTaskCompleted ? Strings.CompleteTaskAnimationButton_ReactivateButtonAriaLabel : Strings.CompleteTaskAnimationButton_CompleteButtonAriaLabel),
            "data-is-focusable": this.props.isFocusZoneEnabled,
            tabIndex: completeTaskButtonTabIndex,
            title: this.props.title || isTaskCompleted ? Strings.CompleteTaskAnimationButton_ReactivateButtonTooltip : Strings.CompleteTaskAnimationButton_CompleteButtonTooltip,
            ref: this.props.completeButtonRef,
            role: "checkbox",
            "aria-checked": isTaskCompleted
        }, /*#__PURE__*/ React.createElement("label", {
            className: classNames.toggleCheckLabel
        }, /*#__PURE__*/ React.createElement(Icon, {
            ...regularCompleteIconProps,
            className: css(regularCompleteIconProps.className, classNames.completeButtonIcon)
        }), /*#__PURE__*/ React.createElement(Icon, {
            ...hoveredCompleteIconProps,
            className: css(hoveredCompleteIconProps.className, classNames.hoveredCompleteButtonIcon)
        })));
    }
    /**
     * The method to set animation data
     * @param isTaskCompleted Whether or not the task is completed - this is the new completed state of the task
     */ setAnimationData(isTaskCompleted) {
        this.setState({
            isTaskCompleted: isTaskCompleted
        });
    }
    /**
     * Called when the button is clicked
     * @param evt The mouse enter event that caused this method to be called.
     */ handleClick(evt) {
        evt?.stopPropagation();
        this.setAnimationData(!this.state.isTaskCompleted);
    }
    /**
     * Callback to play the css animation
     * @param evt The evt that triggered this animation, if any
     * @param callCallback Whether or not to call the onButtonClick callback prop
     */ playCssAnimation(evt, callCallback) {
        if (this.props.isDisabled) {
            return;
        }
        const timeoutDuration = this.state.isTaskCompleted ? this.UNCOMPLETE_ANIMATION_DURATION : this.COMPLETE_ANIMATION_DURATION;
        this.setState({
            isClicked: true
        });
        this.handleClick(evt);
        this.async.setTimeout(()=>{
            this.setState({
                isClicked: false
            });
            callCallback && evt && this.props.onButtonClick(evt);
        }, timeoutDuration); // set a time delay thus animation can be visible before task is completed
    }
    /**
     * Get the classnames associated with the computed styles
     */ getClassNames() {
        const { theme, styles, isDisabled } = this.props;
        const { isTaskCompleted, isClicked } = this.state;
        return getClassNames(getStyles(theme, isTaskCompleted, isClicked, isDisabled ?? false, styles));
    }
    constructor(props, context){
        super(props, context), // Duration of the complete animation in ms
        this.COMPLETE_ANIMATION_DURATION = 600, // Duration of the uncomplete animation in ms
        this.UNCOMPLETE_ANIMATION_DURATION = 100;
        this.state = {
            isTaskCompleted: this.props.isTaskCompleted,
            isClicked: false
        };
    }
}
